import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type { PicklistItemLike } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import {
  type CoachProfileSearch,
  type CoachProfileWithRelevance,
  filterCoachProfiles,
  hasActiveSearch,
  type Sort,
  SORT_OPTIONS,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-profile-search';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

export type { CoachProfileSearch, CoachProfileWithRelevance, Sort };
export {
  filterCoachProfiles,
  matchesSearch,
  SORT_OPTIONS,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-profile-search';

interface CoachesBrowseSignature {
  Args: {
    pool: CoachProfileWithRelevance[];
    searchQuery: string;
    sort: Sort;
    specialty: string | null;
    language: string | null;
    industry: string | null;
    country: string | null;
    onSearchQueryChange: (value: string) => void;
    onSortChange: (value: Sort) => void;
    onSpecialtyChange: (value: string | null) => void;
    onLanguageChange: (value: string | null) => void;
    onIndustryChange: (value: string | null) => void;
    onCountryChange: (value: string | null) => void;
  };
  Blocks: {
    matches: [CoachProfileWithRelevance[]];
  };
  Element: HTMLDivElement;
}

function not(value: boolean): boolean {
  return !value;
}

export class CoachesBrowse extends Component<CoachesBrowseSignature> {
  @service declare readonly picklists: PicklistsService;

  @cached
  get displayedCoaches() {
    return filterCoachProfiles(this.args.pool, this.args, this.args.sort);
  }

  setSearchQuery = (event: Event) => {
    this.args.onSearchQueryChange((event.target as HTMLInputElement).value);
  };

  setSort = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value as Sort;
    this.args.onSortChange(value);
  };

  setSpecialty = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value;
    this.args.onSpecialtyChange(value || null);
  };

  setLanguage = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value;
    this.args.onLanguageChange(value || null);
  };

  setIndustry = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value;
    this.args.onIndustryChange(value || null);
  };

  setCountry = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value;
    this.args.onCountryChange(value || null);
  };

  clearFilters = () => {
    this.args.onSearchQueryChange('');
    this.args.onSortChange('combinedScore');
    this.args.onSpecialtyChange(null);
    this.args.onLanguageChange(null);
    this.args.onIndustryChange(null);
    this.args.onCountryChange(null);
  };

  @cached
  get specialties(): PicklistItemLike[] | undefined {
    const items = this.picklists.groups['coaching-specialisms']?.items;
    return items?.filter((item) =>
      this.args.pool.some((coach) =>
        coach.coachProfile.specialties.includes(item.key)
      )
    );
  }

  @cached
  get industries(): PicklistItemLike[] | undefined {
    const items = this.picklists.groups['coaching-industries']?.items;
    return items?.filter((item) =>
      this.args.pool.some((coach) =>
        coach.coachProfile.industries.includes(item.key)
      )
    );
  }

  @cached
  get languages(): PicklistItemLike[] | undefined {
    const items = this.picklists.groups['coaching-languages']?.items;
    return items?.filter((item) =>
      this.args.pool.some((coach) =>
        coach.coachProfile.languages.some(
          (language) => language.language === item.key
        )
      )
    );
  }

  @cached
  get countries(): PicklistItemLike[] | undefined {
    const items = this.picklists.groups['coaching-countries']?.items;
    return items?.filter((item) =>
      this.args.pool.some((coach) =>
        coach.coachProfile.countries.includes(item.key)
      )
    );
  }

  <template>
    <div class="card border shadow-sm rounded-4" ...attributes>
      <div class="card-body p-4">
        <div class="row g-3 mb-3 align-items-end">
          <div class="col-12 col-md-9">
            <label
              for="coachSearch"
              class="form-label fw-bold small mb-1"
            >Search</label>
            <div class="input-group">
              <span class="input-group-text bg-body border-0">
                <iconify-icon
                  inline
                  icon="ph:magnifying-glass"
                  class="text-body-secondary fs-5"
                  aria-hidden="true"
                />
              </span>
              <input
                id="coachSearch"
                type="search"
                class="form-control bg-body border-0"
                placeholder="Search coaches by name or bio..."
                value={{@searchQuery}}
                {{debounce this.setSearchQuery}}
              />
            </div>
          </div>
          <div class="col-12 col-md-3">
            <label
              for="coachSort"
              class="form-label fw-bold small mb-1"
            >Sort</label>
            <select
              id="coachSort"
              class="form-select"
              {{selectValue @sort}}
              {{on "change" this.setSort}}
            >
              {{#each SORT_OPTIONS as |item|}}
                <option value={{item.value}}>{{item.label}}</option>
              {{/each}}
            </select>
          </div>
        </div>

        <div class="row g-3 align-items-end">
          {{#if this.specialties.length}}
            <div class="col-12 col-md-auto">
              <label
                for="coachSpecialty"
                class="form-label fw-bold small mb-1"
              >Specialty</label>
              <select
                id="coachSpecialty"
                class="form-select"
                {{selectValue @specialty}}
                {{on "change" this.setSpecialty}}
              >
                <option value="">All specialties</option>
                {{#each this.specialties as |specialty|}}
                  <option value={{specialty.key}}>{{specialty.title}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
          {{#if this.industries.length}}
            <div class="col-12 col-md-auto">
              <label
                for="coachIndustry"
                class="form-label fw-bold small mb-1"
              >Industry</label>
              <select
                id="coachIndustry"
                class="form-select"
                {{selectValue @industry}}
                {{on "change" this.setIndustry}}
              >
                <option value="">All industries</option>
                {{#each this.industries as |industry|}}
                  <option value={{industry.key}}>{{industry.title}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
          {{#if this.languages.length}}
            <div class="col-12 col-md-auto">
              <label
                for="coachLanguage"
                class="form-label fw-bold small mb-1"
              >Language</label>
              <select
                id="coachLanguage"
                class="form-select"
                {{selectValue @language}}
                {{on "change" this.setLanguage}}
              >
                <option value="">Any language</option>
                {{#each this.languages as |language|}}
                  <option value={{language.key}}>{{language.title}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
          {{#if this.countries.length}}
            <div class="col-12 col-md-auto">
              <label
                for="coachLocation"
                class="form-label fw-bold small mb-1"
              >Country</label>
              <select
                id="coachLocation"
                class="form-select"
                {{selectValue @country}}
                {{on "change" this.setCountry}}
              >
                <option value="">Any country</option>
                {{#each this.countries as |country|}}
                  <option value={{country.key}}>{{country.title}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
          <div
            class="col-12 col-md text-md-end d-flex flex-wrap align-items-baseline justify-content-md-end column-gap-2 row-gap-1 pb-2"
          >
            <button
              type="button"
              class="btn btn-primary btn-sm text-nowrap"
              {{on "click" this.clearFilters}}
              disabled={{not (hasActiveSearch this.args)}}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    {{yield this.displayedCoaches to="matches"}}
  </template>
}
